import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IntentType } from "@lib/infrastructure";
import { useControlLoader } from "@lib/logic";
import { useOverridableFeatureFlags } from "@lib/store";
import { Loader } from "@atoms";
import { Button } from "@molecules";
import { useIntents, useVerticalScrollToElement } from "@hooks";

import { ControlItem } from "./ControlItem";

const COLLAPSED_COUNT = 4;

/**
 * The blinds and temperature controls of a room or an area, refreshed when they're more than 30 seconds old. An app
 * link to control them scrolls them into view once.
 */
export const ControlsSection = ({ nodeId }: { nodeId: string }) => {
  const { t } = useTranslation();
  const { Controls } = useOverridableFeatureFlags();
  const { controls, status } = useControlLoader(nodeId, true, 30);
  const [showsAll, setShowsAll] = useState(false);

  const isLoading = status === "NotLoaded" || status === "Loading";

  const { linkedIntent, claimHandled } = useIntents();
  const isLinkedTo = linkedIntent?.intent === IntentType.ControlBlindsOrTemperature && !linkedIntent.isHandled;
  const { componentRef } = useVerticalScrollToElement(300, !isLoading, isLinkedTo, claimHandled);

  if (!Controls || controls.length === 0) return null;

  const shownControls = showsAll ? controls : controls.slice(0, COLLAPSED_COUNT);

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-[20px] font-semibold leading-[25px]">{t("controls:Control")}</h3>

      {isLoading ? (
        <div className="flex justify-center">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col items-start">
          <div
            ref={componentRef}
            className="flex w-full flex-col gap-0.5 overflow-hidden rounded-lg">
            {shownControls.map(control => (
              <ControlItem
                key={control.id}
                control={control}
                nodeId={nodeId}
              />
            ))}
          </div>
          {controls.length > COLLAPSED_COUNT && (
            <Button
              className="mt-2.5"
              button="text"
              onClick={() => setShowsAll(!showsAll)}>
              {t(showsAll ? "ShowLess" : "ShowAll")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
};
